import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Download, Share2, Link2, Check, Sparkles, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { usePreviewPng, uploadShareCard, saveImageBlob } from '@/utils/shareCard';
import axios from 'axios';
import { shareViaNative } from '@/utils/nativeShare';
import { SHARE_THEMES, getShareTheme } from '@/components/shareThemes';

import { useTranslation } from 'react-i18next';

/**
 * ShareDYKModal — Did You Know share-card modal.
 *
 * Mirrors ShareReflectionModal exactly:
 *   • Themed swatch picker (unified SHARE_THEMES palette)
 *   • Live in-modal card preview (matches the server-rendered OG PNG)
 *   • Four action buttons: Share Image • Save • Link • Friend
 *
 * Backend share record is created on mount so the Link / Image-share
 * URL is ready instantly when the user taps the action. The link points
 * at `/api/og/share/{dyk_share_id}` which serves OG-tagged HTML for
 * crawlers and 302s humans to /share/dyk/{id}.
 */
function stripMarkdown(t) {
  if (!t) return '';
  return t
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .trim();
}

/** Card rendered into the PNG. Same visual language as the backend PIL renderer. */
function DYKShareCard({ theme, fact }) {
  const {
    t
  } = useTranslation();

  const title = fact?.title || 'Did You Know?';
  const body = stripMarkdown(fact?.content || '');
  return (
    <div
      style={{
        background: `linear-gradient(180deg, ${theme.bg} 0%, ${theme.border} 100%)`,
        padding: '36px 32px 28px',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        minHeight: 200,
      }}
    >
      {/* Badge */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 14px',
          borderRadius: 999,
          background: theme.tagBg,
          color: theme.accent,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          marginBottom: 18,
        }}
      >
        {t('generated.sharedykmodal_did_you_know_c0df')}
      </div>
      {/* Title — same modern sans-serif as the Daily Reflection share
          card. Previously used "Playwrite ES Deco" cursive which read
          poorly on the share preview. */}
      <h2
        style={{
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.25,
          color: theme.topicColor,
          margin: '0 0 14px 0',
        }}
      >
        {title}
      </h2>
      {/* Body */}
      <p
        style={{
          fontSize: 13,
          lineHeight: 1.7,
          color: theme.text,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          margin: '0 0 18px 0',
        }}
      >
        {body}
      </p>
      {/* Footer */}
      <div
        style={{
          paddingTop: 12,
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src="/logo.png"
            alt={t('generated.sharedykmodal_eili_e9c6')}
            style={{ width: 28, height: 28, objectFit: 'contain' }}
            crossOrigin="anonymous"
          />
          <span
            style={{
              fontFamily: "'Playwrite ES Deco', cursive",
              fontSize: 14,
              fontWeight: 700,
              color: theme.accent,
              marginTop: 3,
            }}
          >
            {t('generated.sharedykmodal_eili_e9c6')}
          </span>
        </div>
        <span style={{ fontSize: 10, color: theme.secondary }}>
          {t('generated.sharedykmodal_did_you_know_950c')}
        </span>
      </div>
    </div>
  );
}

export default function ShareDYKModal({ fact, onClose, onShareToFriend, onPostToFeed }) {
  const {
    t
  } = useTranslation();

  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [generating, setGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const cardRef = useRef(null);

  const theme = getShareTheme(selectedTheme);
  const apiBase = `${window.location.origin}/api`;

  // Mint the public share record once on mount.
  useEffect(() => {
    let alive = true;
    axios
      .post(
        `${apiBase}/did-you-know/share`,
        { fact_id: fact?.fact_id, theme: selectedTheme },
        { withCredentials: true, timeout: 8000 },
      )
      .then((r) => { if (alive) setShareInfo(r.data); })
      .catch(() => {
        if (alive) toast.error(t('generated.sharedykmodal_could_not_create_84c9'));
      });
    return () => { alive = false; };
    // selectedTheme intentionally NOT a dep — we don't re-mint when the
    // user previews a different theme. The OG image endpoint accepts
    // ?theme= so the same share_id can render in any theme.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = shareInfo
    ? `${window.location.origin}/api/og/share/${shareInfo.share_id}`
    : null;

  // Official PNG = capture of the live preview for the current theme only,
  // so buttons stay disabled until that exact theme is ready.
  const pngBlob = usePreviewPng(cardRef, `${selectedTheme}|${fact?.fact_id}`, 1080);
  const preparedFile = useMemo(() => (pngBlob
    ? new File([pngBlob], `eili-fact-${fact?.fact_id || 'card'}-${selectedTheme}.png`, { type: 'image/png' })
    : null), [pngBlob]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShareImage = async () => {
    if (!preparedFile) return;
    setGenerating(true);
    if (shareInfo) uploadShareCard(shareInfo.share_id, preparedFile);
    try {
      const text = `Did You Know? ${fact?.title || ''}`;
      const result = await shareViaNative({
        title: t('generated.sharedykmodal_did_you_know_458c'),
        text,
        url: shareUrl,
        file: preparedFile,
      });
      if (!result?.aborted && !result?.shared) {
        toast.info(t('generated.sharedykmodal_use_save_or_ca7c'));
      }
    } catch {
      toast.error(t('generated.sharedykmodal_could_not_open_566d'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => saveImageBlob(preparedFile, preparedFile?.name || `eili-fact-${Date.now()}.png`);

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    if (preparedFile) uploadShareCard(shareInfo.share_id, preparedFile);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error(t('generated.sharedykmodal_could_not_copy_d8cc'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      data-testid="share-dyk-modal"
    >
      <div
        className="relative w-full max-w-lg flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          maxHeight: 'min(90vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.sharedykmodal_share_did_you_3f2a')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition hover:bg-white/10"
            data-testid="share-dyk-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Theme picker */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharedykmodal_style_151d')}
            </p>
            <div className="flex gap-2.5 flex-wrap" data-testid="share-dyk-theme-picker">
              {SHARE_THEMES.map((t) => {
                const active = selectedTheme === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setSelectedTheme(t.key)}
                    className="flex flex-col items-center gap-1.5 transition-all"
                    data-testid={`share-dyk-theme-${t.key}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full relative overflow-hidden transition-all"
                      style={{
                        background: t.swatch[0],
                        border: active ? `2.5px solid ${t.swatch[1]}` : '2px solid transparent',
                        boxShadow: active ? `0 0 0 2px ${t.swatch[1]}40` : 'none',
                      }}
                    >
                      <div
                        className="absolute bottom-0 right-0 w-5 h-5 rounded-tl-full"
                        style={{ background: t.swatch[1] }}
                      />
                    </div>
                    <span
                      className="text-[10px] font-medium"
                      style={{ color: active ? 'var(--theme-text)' : 'var(--theme-textSecondary)' }}
                    >
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharedykmodal_preview_271d')}
            </p>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--glass-border)' }}>
              <div ref={cardRef} data-testid="share-dyk-preview">
                <DYKShareCard theme={theme} fact={fact} />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t flex flex-wrap gap-2.5" style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <Button
            onClick={handleShareImage}
            disabled={generating || !preparedFile}
            className="flex-1 min-w-[120px] rounded-xl py-4 font-medium gap-2"
            style={{ background: theme.accent, color: '#fff' }}
            data-testid="share-dyk-image-btn"
          >
            {!preparedFile ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('generated.sharedykmodal_preparing_7d76')}
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                {generating ? 'Generating…' : (document.body?.classList?.contains('despia-android') ? 'Share' : 'Share Image')}
              </>
            )}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!preparedFile}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-dyk-download-btn"
          >
            <Download className="h-4 w-4" />
            {t('generated.sharedykmodal_save_1618')}
          </Button>
          <Button
            onClick={handleCopyLink}
            disabled={!shareUrl || !preparedFile}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-dyk-copy-link-btn"
          >
            {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {linkCopied ? 'Copied' : 'Link'}
          </Button>
          {onShareToFriend && (
            <Button
              onClick={() => { onShareToFriend?.(); onClose(); }}
              variant="outline"
              className="rounded-xl py-4 font-medium gap-2"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
              data-testid="share-dyk-to-friend-btn"
            >
              <Users className="h-4 w-4" />
              {t('generated.sharedykmodal_friend_0d1d')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
